/* Global imports. */
import React, { useRef, useState, useEffect } from "react" 
import { Link, navigate } from "gatsby"  
import ContributorLink from "@components/ContributorLink";

function SearchBar() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState("")
  const [newsResults, setNewsResults] = useState("[]")
  const [editionResults, setEditionResults] = useState("")
  const [contributorResults, setContributorResults] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const maxResults = 5
  const searchInput = useRef(null)
  const searchWrapper = useRef()
  const searchFunctionUrl = `/api/search`

  const focus = () => {
    searchInput.current.focus();
  };

  useEffect(() => {
    if(query !== "") {
      setIsLoadingResults(true)
    }
  }, [query])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if(query.length >= 3) {
        fetch(`${searchFunctionUrl}?term=${query}`)
        .then(response => {
          return response.json()
        })
        .then(data => {
          let allResults = data.results
          setResults(allResults)

          if (typeof window !== undefined) {
            window.dataLayer = window.dataLayer || []
            
            window.dataLayer.push({
              'event':'view_search_results',
              'search_term':`${query}`
            })
          }
  
          let tempNewsResults = allResults
          let tempEditionResults = allResults
          let tempContributorResults = allResults
  
          if(allResults.length > 0) {
            setNewsResults(tempNewsResults.filter(item => item.type === 'article'))
            setEditionResults(tempEditionResults.filter(item => item.type === 'PanMacmillanEdition'))
            setContributorResults(tempContributorResults.filter(item => item.type === 'PanMacmillanContributor'))
          } else {
            console.log("no results")
          }

          setIsLoadingResults(false)
        }).catch(error => {
          console.error('There has been a problem with your fetch operation:', error);
        });
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
    
  }, [query]);

  const getLink = (page) => {
    if(page.type === "PanMacmillanEdition") {
      return <Link className="font-serif no-underline text-red" data-title={page.title} data-author={page.primaryAuthor} data-isbn={page.isbn} to={"/" + page.path}><span dangerouslySetInnerHTML={{ __html: page.title }} /></Link>
    } else if(page.type === "PanMacmillanContributor") {
      return <ContributorLink className="font-serif no-underline text-red" contributorName={page.title} url={page.path} />
    } else if (page.type === "article") {
      return <a className="font-serif no-underline text-red" href={"/" + page.path}><span dangerouslySetInnerHTML={{ __html: page.title }} /></a>
    } 
  }

  const renderResults = (results) => {
    return results.slice(0, maxResults).map(item => (  
      item.title !== undefined && 
        <li key={item.id} className="py-1 leading-tight">
          { item.title !== undefined && getLink(item) }
        </li>  
    ))
  }

  const searchButtonClick = () => {
    if(query.length > 0) {
      setIsOpen(false)

      if (typeof window !== undefined) {
        window.dataLayer = window.dataLayer || []
        
        window.dataLayer.push({
          'event':'select_search_result',
          'search_term':`${query}`,
          'search_type':'search_button'
        })
      }

      navigate(`/search?searchTerm=${query}`)
    }
  }
  
  const search = evt => {  
    const searchQuery = evt.target.value  
    setQuery(searchQuery)
  }

  useEffect(() => {
    const handleClick = e => {
        if (searchWrapper.current.contains(e.target)) {
            // Only set isOpen to true when clicking the search icon
            if (e.target.closest('.js-search-trigger')) {
                setIsOpen(!isOpen);
            }
            // Don't close when clicking inside the wrapper
            return;
        }
        // Close when clicking outside
        setIsOpen(false);
    };

    // add when mounted
    document.addEventListener("mousedown", handleClick);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isOpen]);

  return (
    <li className={`nav-list__search !h-full js-search-wrapper relative ${isOpen ? "nav-list__search--open" : ""}`} ref={searchWrapper}>
      <div className="nav-search-trigger">
        <span className="cursor-pointer js-search-trigger" onClick={focus}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 57 57"><path style={{fill:"#FFF"}} d="M55.1 51.9L41.6 37.8C45.1 33.6 47 28.4 47 23 47 10.3 36.7 0 24 0S1 10.3 1 23s10.3 23 23 23c4.8 0 9.3-1.4 13.2-4.2L50.8 56c.6.6 1.3.9 2.2.9.8 0 1.5-.3 2.1-.8 1.2-1.1 1.2-3 0-4.2zM24 6c9.4 0 17 7.6 17 17s-7.6 17-17 17S7 32.4 7 23 14.6 6 24 6z"></path></svg>
        </span>
        <div className="max-w-[600px] rounded-bl-md rounded-br-md h-[57px] absolute shadow-lg nav-search-trigger__search-input !bottom-0">
          <div className="flex flex-col w-full autocomplete-block js-autocomplete-module autocomplete-block--nav autocomplete-block--visible bg-white rounded-b-md">
            <div className="flex w-full">
			<input aria-label="Search" id="search-input" className="w-full h-auto p-4 font-sans border-0 border-[#d9d9d9] rounded-bl-md focus:outline-none" placeholder="Search" type="text" value={query} onChange={search} ref={searchInput}  />  
            <input type="submit" className="cursor-pointer font-sans p-4 border-0 rounded-br-md text-sm bg-[#95c11f] hover:bg-[#779a19] text-white text-center w-[100px]" onClick={searchButtonClick} value="Search"/>             
            </div>
			{isLoadingResults ? 
              <ul className="w-full block max-h-[560px] overflow-auto bg-white border  border-[#D9D9D9] rounded-b-md border-t-0 drop-shadow-lg min-h-[128px] pt-5 px-4 pb-4 autocomplete-results js-autocomplete-list autocomplete-results--visible">
                <li className="py-4 text-center"><div className="loader"></div> Retrieving results for {query}</li>
              </ul> 
              : 
              <>
              {results.length > 0 && 
              <ul className="w-full block max-h-[560px] overflow-auto bg-white border border-[#D9D9D9] rounded-md border-t-0 drop-shadow-lg min-h-[128px] pt-5 px-4 pb-4 autocomplete-results js-autocomplete-list autocomplete-results--visible">  
                {editionResults.length > 0 && (
                  <h2 className="p-0 !mb-0 font-sans text-2xl">Books</h2>
                )}
                {editionResults.length > 0 && renderResults(editionResults)}
                {editionResults.length > maxResults && 
                <p style={{marginBottom: 10 + 'px'}}><strong><a href={"/search?searchTerm=" + query} onClick={()=>setIsOpen(false)} className="font-serif no-underline text-red">{editionResults.length - maxResults} more book results for '{query}'</a></strong></p>
                }
                {contributorResults.length > 0 && (
                  <h2 className="p-0 !mb-0 font-sans text-2xl">Authors & Illustrators</h2>
                )}
                {contributorResults.length > 0 && renderResults(contributorResults)}
                {contributorResults.length > maxResults && 
                <p style={{marginBottom: 10 + 'px'}}><strong><a href={"/search?searchTerm=" + query} onClick={()=>setIsOpen(false)}  className="font-serif no-underline text-red">{contributorResults.length - maxResults} more author &amp; illustrator results for '{query}'</a></strong></p>
                }
                {newsResults.length > 0 && (
                  <h2 className="p-0 !mb-0 font-sans text-2xl">Articles</h2>
                )}
                {newsResults.length > 0 && renderResults(newsResults)}
                {newsResults.length > maxResults && 
                <p style={{marginBottom: 10 + 'px'}}><strong><a href={"/search?searchTerm=" + query} onClick={()=>setIsOpen(false)} className="font-serif no-underline text-red">{newsResults.length - maxResults} more article results for '{query}'</a></strong></p>
                }
              </ul> 
              } 
              </>
            }
        </div>
        </div>  
      </div>
    </li>
  )
}

export default SearchBar;
